import { useState } from 'react';
import { Link } from '@tanstack/react-router';
import { ArrowLeft, Mail, MessageSquare, MapPin, Clock, Send, Loader2, CheckCircle, Github } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Button, Card, CardHeader, CardTitle, CardContent, Input } from '../../shared/ui';

/**
 * Contact Page
 * Public page with contact form and info
 */
export function ContactPage() {
    const { t } = useTranslation();
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        subject: 'general',
        message: '',
    });
    const [submitted, setSubmitted] = useState(false);
    const [sending, setSending] = useState(false);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setSending(true);
        await new Promise((r) => setTimeout(r, 1500));
        setSending(false);
        setSubmitted(true);
    };

    const contactInfo = [
        {
            icon: <Mail className="h-5 w-5" />,
            labelKey: 'contact.info.email',
            value: 'support@devlint.io',
            href: 'mailto:support@devlint.io',
        },
        {
            icon: <Github className="h-5 w-5" />,
            labelKey: 'contact.info.github',
            value: 'github.com/devlintHub',
            href: 'https://github.com/devlintHub',
        },
        {
            icon: <Clock className="h-5 w-5" />,
            labelKey: 'contact.info.supportLabel',
            valueKey: 'contact.info.supportHours',
            href: null,
        },
        {
            icon: <MapPin className="h-5 w-5" />,
            labelKey: 'contact.info.locationLabel',
            valueKey: 'contact.info.location',
            href: null,
        },
    ];

    return (
        <div className="min-h-screen bg-background">
            {/* Header */}
            <header className="sticky top-0 z-50 border-b border-border bg-background px-8 py-4">
                <div className="mx-auto max-w-5xl flex items-center justify-between">
                    <div className="flex items-center gap-4">
                        <Link to="/">
                            <Button variant="ghost" size="sm" corners="asymmetric" icon={<ArrowLeft className="h-4 w-4" />}>
                                {t('common.back')}
                            </Button>
                        </Link>
                    </div>
                    <div className="flex items-center gap-3">
                        <img src="/favicon-32x32.png" alt="DevLint" className="h-8 w-8" />
                        <span className="font-medium text-text-primary font-mono">DevLint</span>
                    </div>
                </div>
            </header>

            <main className="mx-auto max-w-5xl px-8 py-12">
                {/* Page Title */}
                <div className="mb-12 text-center">
                    <div className="inline-flex items-center justify-center h-16 w-16 rounded-asymmetric-lg bg-surface-hover border border-border mb-6">
                        <MessageSquare className="h-8 w-8 text-primary" />
                    </div>
                    <h1 className="text-4xl font-bold text-text-primary font-mono tracking-tight">
                        {t('contact.title')}
                    </h1>
                    <p className="mt-4 text-text-muted text-lg leading-relaxed">
                        {t('contact.subtitle')}
                    </p>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                    {/* Contact Form */}
                    <div className="lg:col-span-2">
                        <Card padding="lg" corners="asymmetric">
                            <CardHeader>
                                <CardTitle className="flex items-center gap-3">
                                    <Send className="h-5 w-5 text-primary" />
                                    {t('contact.form.title')}
                                </CardTitle>
                            </CardHeader>
                            <CardContent>
                                {submitted ? (
                                    <div className="py-12 text-center">
                                        <div className="flex justify-center mb-6">
                                            <div className="h-16 w-16 rounded-full bg-success/10 flex items-center justify-center">
                                                <CheckCircle className="h-8 w-8 text-success" />
                                            </div>
                                        </div>
                                        <h3 className="text-xl font-semibold text-text-primary font-mono mb-2">
                                            {t('contact.form.successTitle')}
                                        </h3>
                                        <p className="text-text-muted text-lg leading-relaxed">
                                            {t('contact.form.successMessage')}
                                        </p>
                                        <div className="flex justify-center mt-6">
                                            <Button
                                                variant="outline"
                                                corners="asymmetric"
                                                onClick={() => {
                                                    setSubmitted(false);
                                                    setFormData({ name: '', email: '', subject: 'general', message: '' });
                                                }}
                                            >
                                                {t('contact.form.sendAnother')}
                                            </Button>
                                        </div>
                                    </div>
                                ) : (
                                    <form onSubmit={handleSubmit} className="space-y-5">
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                                            <div>
                                                <label className="block text-sm font-medium text-text-primary font-mono mb-2">
                                                    {t('contact.form.name')}
                                                </label>
                                                <Input
                                                    value={formData.name}
                                                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                                    placeholder={t('contact.form.namePlaceholder')}
                                                    required
                                                />
                                            </div>
                                            <div>
                                                <label className="block text-sm font-medium text-text-primary font-mono mb-2">
                                                    {t('contact.form.email')}
                                                </label>
                                                <Input
                                                    type="email"
                                                    value={formData.email}
                                                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                                                    placeholder={t('contact.form.emailPlaceholder')}
                                                    required
                                                />
                                            </div>
                                        </div>

                                        <div>
                                            <label className="block text-sm font-medium text-text-primary font-mono mb-2">
                                                {t('contact.form.subject')}
                                            </label>
                                            <select
                                                value={formData.subject}
                                                onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                                                className="
                          w-full px-4 py-3 rounded-asymmetric-md
                          bg-surface border border-border
                          text-text-primary font-mono text-sm
                          focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent
                          transition-all duration-200 cursor-pointer
                        "
                                            >
                                                <option value="general">{t('contact.form.subjects.general')}</option>
                                                <option value="support">{t('contact.form.subjects.support')}</option>
                                                <option value="billing">{t('contact.form.subjects.billing')}</option>
                                                <option value="bug">{t('contact.form.subjects.bug')}</option>
                                                <option value="feature">{t('contact.form.subjects.feature')}</option>
                                                <option value="partnership">{t('contact.form.subjects.partnership')}</option>
                                            </select>
                                        </div>

                                        <div>
                                            <label className="block text-sm font-medium text-text-primary font-mono mb-2">
                                                {t('contact.form.message')}
                                            </label>
                                            <textarea
                                                value={formData.message}
                                                onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                                                placeholder={t('contact.form.messagePlaceholder')}
                                                rows={5}
                                                required
                                                className="
                          w-full px-4 py-3 rounded-asymmetric-md
                          bg-surface border border-border
                          text-text-primary font-mono text-sm
                          focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent
                          transition-all duration-200 resize-none
                          placeholder:text-text-muted
                        "
                                            />
                                        </div>

                                        <div className="flex items-center gap-3 pt-2">
                                            <Button
                                                variant="primary"
                                                corners="asymmetric"
                                                type="submit"
                                                disabled={sending}
                                                icon={sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                                            >
                                                {sending ? t('contact.form.sending') : t('contact.form.send')}
                                            </Button>
                                        </div>
                                    </form>
                                )}
                            </CardContent>
                        </Card>
                    </div>

                    {/* Contact Info Sidebar */}
                    <div className="space-y-6">
                        <Card padding="lg" corners="asymmetric-inverse">
                            <CardHeader>
                                <CardTitle className="text-base">{t('contact.info.title')}</CardTitle>
                            </CardHeader>
                            <CardContent>
                                <div className="space-y-5">
                                    {contactInfo.map((info, i) => (
                                        <div key={i} className="flex items-start gap-3">
                                            <span className="inline-flex h-10 w-10 items-center justify-center rounded-asymmetric-md bg-surface-hover border border-border text-primary flex-shrink-0">
                                                {info.icon}
                                            </span>
                                            <div>
                                                <p className="text-xs text-text-muted font-mono uppercase tracking-wide">{t(info.labelKey)}</p>
                                                {info.href ? (
                                                    <a
                                                        href={info.href}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        className="text-sm text-text-primary hover:text-primary transition-colors font-mono"
                                                    >
                                                        {info.value}
                                                    </a>
                                                ) : (
                                                    <p className="text-sm text-text-primary font-mono">{'valueKey' in info && info.valueKey ? t(info.valueKey) : info.value}</p>
                                                )}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </CardContent>
                        </Card>

                        {/* FAQ Quick Links */}
                        <Card padding="lg" corners="asymmetric">
                            <CardHeader>
                                <CardTitle className="text-base">{t('contact.faq.title')}</CardTitle>
                            </CardHeader>
                            <CardContent>
                                <div className="space-y-3">
                                    {(['storage', 'cancel', 'languages'] as const).map((key) => (
                                        <div key={key} className="rounded-asymmetric-md border border-border bg-surface p-3">
                                            <p className="text-sm font-medium text-text-primary font-mono">{t(`contact.faq.${key}.question`)}</p>
                                            <p className="text-xs text-text-muted mt-1">{t(`contact.faq.${key}.answer`)}</p>
                                        </div>
                                    ))}
                                </div>
                            </CardContent>
                        </Card>
                    </div>
                </div>
            </main>

            {/* Footer */}
            <footer className="border-t border-border py-8 mt-12">
                <div className="mx-auto max-w-5xl px-8">
                    <div className="flex flex-col items-center justify-between gap-4 md:flex-row">
                        <p className="text-sm text-text-muted font-mono">{t('landing.footer.copyright')}</p>
                        <div className="flex gap-6 text-sm text-text-muted font-mono">
                            <Link to="/privacy" className="hover:text-text-primary transition-colors">{t('landing.footer.privacy')}</Link>
                            <Link to="/terms" className="hover:text-text-primary transition-colors">{t('landing.footer.terms')}</Link>
                        </div>
                    </div>
                </div>
            </footer>
        </div>
    );
}
